/**
 * Helpers for `unitAzimuthOverridesAtom`: how a stated LTD bearing is keyed, and
 * how far a frame drawn from a tile has to turn to sit on it.
 */

/**
 * The atom key for one unit in one workspace. Scoped by workspace so a bearing
 * stated for a unit in one workspace never turns the same unit in another.
 *
 * @param {number | string | null | undefined} workspaceId - Inventory or Type Curve workspace id.
 * @param {number} unitId - development_areas.id.
 * @return {string} Stable map key.
 */
export function azimuthOverrideKey(
  workspaceId: number | string | null | undefined,
  unitId: number
): string {
  return `${workspaceId ?? 'none'}:${unitId}`;
}

/**
 * The turn, in degrees, that takes a frame drawn at `bakedDeg` onto the axis of
 * `statedDeg`. Bearings are compared as AXES — a lateral at 269° and one at 89°
 * run the same way — so the answer is folded into (-90, 90]. Exactly 90 stays
 * 90 (the UI's rotate), the same axis is 0, and a tile that already bakes the
 * stated bearing therefore needs no turn at all.
 *
 * @param {number} bakedDeg - What the tile bakes for the unit.
 * @param {number} statedDeg - What the server last stated.
 * @return {number} Degrees to add to the drawn frame's azimuth.
 */
export function axisDelta(bakedDeg: number, statedDeg: number): number {
  const d = (((statedDeg - bakedDeg) % 180) + 180) % 180;
  return d > 90 ? d - 180 : d;
}
